import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, ClipboardCheck, CalendarClock, Database, AlertCircle, FileText } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminGovernance() {
  const [stats, setStats] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [recentAudit, setRecentAudit] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/policies").then((r) => setPolicies(r.data));
    api.get("/admin/audit-log?limit=8").then((r) => setRecentAudit(r.data));
  }, []);

  return (
    <div data-testid="admin-governance">
      <span className="eyebrow">Live Governance Status</span>
      <h1 className="mt-2 text-3xl text-navy">Platform Self-Governance</h1>
      <span className="divider-gold" />
      <p className="text-navy/70 max-w-2xl">
        Hawkam applies governance to its own platform. This dashboard reflects the live state of compliance,
        data management, and audit trails.
      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatusCard icon={ShieldCheck} title="GDPR" value={stats?.gdpr_status || "compliant"} tone="ok" />
        <StatusCard icon={ClipboardCheck} title="Consents" value={stats?.consents ?? "—"} tone="ok" />
        <StatusCard icon={CalendarClock} title="Last audit entry" value={stats?.last_audit_at ? new Date(stats.last_audit_at).toLocaleString() : "—"} tone="ok" />
        <StatusCard icon={Database} title="Audit entries" value={stats?.audit_entries ?? "—"} tone="ok" />
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-elev p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl text-navy">Governance Documents ({policies.length})</h2>
            <Link to="/admin/policies" className="text-navy/70 hover:text-gold text-sm">Manage →</Link>
          </div>
          <ul className="mt-4 divide-y divide-navy/10">
            {policies.map((p) => (
              <li key={p.key} className="py-3 flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-navy"><FileText size={14} className="text-gold" /> {p.title_en}</span>
                <span className="text-xs text-navy/60">v{p.version}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-elev p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl text-navy">Recent Audit Activity</h2>
            <Link to="/admin/audit-log" className="text-navy/70 hover:text-gold text-sm">Full log →</Link>
          </div>
          <ul className="mt-4 divide-y divide-navy/10">
            {recentAudit.map((r) => (
              <li key={r.id} className="py-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="chip text-[10px] !bg-white !text-navy !border-navy/20">{r.category}</span>
                  <span className="text-navy">{r.action}</span>
                  <span className="ms-auto text-xs text-navy/60">{r.timestamp?.slice(11, 19)}</span>
                </div>
                <div className="text-xs text-navy/60 mt-1">{r.actor}</div>
              </li>
            ))}
            {recentAudit.length === 0 && <li className="py-4 text-center text-navy/50 text-sm">No recent activity</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ icon: Icon, title, value, tone }) {
  return (
    <div className="card-elev p-5">
      <div className={`text-${tone === "ok" ? "gold" : "red-500"}`}><Icon size={20} /></div>
      <div className="mt-3 text-lg text-navy">{value}</div>
      <div className="text-xs uppercase tracking-widest text-navy/60 mt-1">{title}</div>
    </div>
  );
}
